import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  VlFeedbackRequest,
  VlFeedbackResponse,
  VlLotDetail,
  VlLotsQuery,
  VlLotsResponse,
  VlAuctionReadingsQuery,
  VlAuctionReadingsResponse,
  VlCommentRequest,
  VlResearchItem,
  VlResearchNoteItem,
  VlResearchNotesResponse,
  VlResearchQuery,
  VlResearchResponse,
  VlResearchReviewRequest,
  VlSource,
  VlSummary,
  VlSummaryQuery,
  VlValueResponse,
} from '../models/valuationLab';
import { SessionService } from './session-service';
import { localDevAccess } from 'src/app/dev-access';

/** Why a Valuation Lab call failed — drives the component's error copy. */
export type VlFailure = 'not_admin' | 'unreachable' | 'unauthorised' | 'http' | 'server';

export class VlError extends Error {
  constructor(public kind: VlFailure, message: string, public status: number | null = null) {
    super(message);
  }
}

/**
 * Admin-only client for the Valuation Lab routes on the PlateX assistant
 * server (environment.assistantUrl). Same auth pattern as the auction
 * estimates in live-auctions.component.ts: fetch() with the PlateX bearer
 * token, sent only for admins; fetch() bypasses the token interceptor, so on
 * 401 the session is refreshed once and the call retried.
 *
 * Contract: platex-valuation/docs/VALUATION_LAB_API.md. Every method resolves
 * with the parsed JSON or rejects with a VlError.
 */
@Injectable({ providedIn: 'root' })
export class ValuationLabService {
  constructor(private sessionService: SessionService) {}

  get baseUrl(): string {
    return (((environment as any).assistantUrl as string) || '').replace(/\/$/, '');
  }

  /** True when this session can call the lab at all (admin + configured host). */
  get available(): boolean {
    return !!this.baseUrl && (this.sessionService.isAdmin() || localDevAccess);
  }

  public summary(q: VlSummaryQuery = {}): Promise<VlSummary> {
    return this.get<VlSummary>('/valuation-lab/summary', {
      source: q.source || 'all',
      house: q.house,
      style: q.style,
    });
  }

  public lots(q: VlLotsQuery = {}): Promise<VlLotsResponse> {
    return this.get<VlLotsResponse>('/valuation-lab/lots', {
      source: q.source || 'all',
      house: q.house,
      status: q.status,
      style: q.style,
      length: q.length,
      segment_type: q.segment_type,
      segment_key: q.segment_key,
      q: q.q,
      sort: q.sort,
      page: q.page,
      page_size: q.page_size,
      outside: q.outside,
    });
  }

  public lot(lotKey: string, source: VlSource | string): Promise<VlLotDetail> {
    return this.get<VlLotDetail>('/valuation-lab/lot', { lot_key: lotKey, source });
  }

  public value(plate: string, asOf: string | null): Promise<VlValueResponse> {
    return this.get<VlValueResponse>('/valuation-lab/value', { plate, as_of: asOf || undefined });
  }

  public feedback(body: VlFeedbackRequest): Promise<VlFeedbackResponse> {
    return this.request<VlFeedbackResponse>('/valuation-lab/feedback', {
      method: 'POST',
      body: JSON.stringify(body),
      contentType: 'application/json',
    });
  }

  // ------------------------------------------------------------ research queue
  // Prices in research items are WHOLE POUNDS (not pence).

  public research(q: VlResearchQuery = {}): Promise<VlResearchResponse> {
    return this.get<VlResearchResponse>('/valuation-lab/research', {
      status: q.status,
      q: q.q,
      category: q.category,
      page: q.page,
      page_size: q.page_size,
    });
  }

  public researchItem(plate: string): Promise<VlResearchItem> {
    return this.get<VlResearchItem>('/valuation-lab/research/item', { plate });
  }

  public researchReview(body: VlResearchReviewRequest): Promise<{ ok: boolean; id: string }> {
    return this.request<{ ok: boolean; id: string }>('/valuation-lab/research/review', {
      method: 'POST',
      body: JSON.stringify(body),
      contentType: 'application/json',
    });
  }

  /** Owner comment on how a lot was valued; Claude reviews and replies. */
  public comment(body: VlCommentRequest): Promise<{ ok: boolean; id: string }> {
    return this.request<{ ok: boolean; id: string }>('/valuation-lab/comment', {
      method: 'POST',
      body: JSON.stringify(body),
      contentType: 'application/json',
    });
  }

  /** Upcoming-auction lots with their (pre-sale) meaning readings. */
  public auctionReadings(q: VlAuctionReadingsQuery = {}): Promise<VlAuctionReadingsResponse> {
    return this.get<VlAuctionReadingsResponse>('/valuation-lab/auction-readings', {
      house: q.house,
      has_reading: q.has_reading,
      review: q.review,
      q: q.q,
      page: q.page,
      page_size: q.page_size,
    });
  }

  /** Owner batch entry: notes ("why it sold high") and/or send-to-research requests. */
  public researchNotes(items: VlResearchNoteItem[]): Promise<VlResearchNotesResponse> {
    return this.request<VlResearchNotesResponse>('/valuation-lab/research/notes', {
      method: 'POST',
      body: JSON.stringify({ items }),
      contentType: 'application/json',
    });
  }

  // ------------------------------------------------------------------ plumbing

  private get<T>(path: string, params: Record<string, string | number | null | undefined>): Promise<T> {
    const qs = Object.keys(params)
      .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '')
      .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(String(params[k])))
      .join('&');
    return this.request<T>(path + (qs ? '?' + qs : ''), { method: 'GET' });
  }

  private headers(contentType?: string): HeadersInit | null {
    if (!this.available) return null;
    const h: Record<string, string> = { Authorization: 'Bearer ' + this.sessionService.getToken() };
    if (contentType) h['Content-Type'] = contentType;
    return h;
  }

  private request<T>(
    path: string,
    opts: { method: 'GET' | 'POST'; body?: string; contentType?: string },
    retried = false
  ): Promise<T> {
    const headers = this.headers(opts.contentType);
    if (!headers) {
      return Promise.reject(new VlError('not_admin', 'Valuation Lab is for PlateX admins only.'));
    }
    return fetch(this.baseUrl + path, { method: opts.method, headers, body: opts.body })
      .catch(() => {
        throw new VlError('unreachable', 'Valuation Lab data unavailable');
      })
      .then((r: Response) => {
        if (r.status === 401) {
          // fetch() skips the token interceptor: refresh the session once and retry.
          if (!retried && this.sessionService.isLoggedIn()) {
            return new Promise<T>((resolve, reject) => {
              this.sessionService.RefreshToken().subscribe({
                next: () => this.request<T>(path, opts, true).then(resolve, reject),
                error: () => reject(new VlError('unauthorised', 'Your PlateX session has expired — sign in again.', 401)),
              });
            });
          }
          throw new VlError('unauthorised', 'Your PlateX session has expired — sign in again.', 401);
        }
        return r
          .json()
          .catch(() => null)
          .then((j: any) => {
            if (!r.ok) {
              const msg = j && typeof j.error === 'string' ? j.error : 'Valuation Lab returned HTTP ' + r.status;
              throw new VlError(r.status === 403 ? 'not_admin' : 'http', msg, r.status);
            }
            if (j === null || j === undefined) {
              throw new VlError('server', 'Valuation Lab returned an empty response', r.status);
            }
            // A whole-response {"error": …} (as opposed to a per-method one).
            if (typeof j.error === 'string' && Object.keys(j).length <= 2 && !j.comp && !j.stat) {
              throw new VlError('server', j.error, r.status);
            }
            return j as T;
          });
      });
  }
}
